import * as React from 'react'
import { useNavigate } from 'react-router-dom-v5-compat';

import TruncatedDescription from '../truncated_description'
import { alphabeticalName, } from '../../../../../../modules/sortingMethods'
import { ClassroomInterface, SkillInterface, StateStandardInterface, StateStandardPerformanceData, } from '../interfaces';
import {
  Spinner,
  EXTRA_SMALL_ICON_BASE_SRC,
  Tooltip,
  pluralize,
  titleCaseMultiWordString,
} from '../../../../../Shared/index';
import { NEEDS_MORE_PRACTICE, MEETING_GOAL, APPROACHING_GOAL, NEEDS_SUPPORT, getSmartGroup, smartGroupToClassName, formatMomentumScore, getSortableSmartGroup, ORDERED_SMART_GROUPS, formatLastPracticed, } from '../../shared/shared';
import { formatGradeLevelWithContext, sortGradeLevels, NoStudentsEmptyState, classroomHasNoStudents, } from '../shared';
import { useAllSkillCategoriesAndSubCategoriesContext, } from '../all_skill_categories_and_sub_categories'

const helpIconSrc = `${EXTRA_SMALL_ICON_BASE_SRC}/help.svg`;

interface ByStandardProps {
  classroom: ClassroomInterface;
  momentumScoreGoal: number;
  data: StateStandardPerformanceData[] | null;
  selectedStateStandard?: StateStandardInterface;
  selectedGradeLevel?: string;
  studentId?: number | string;
}

interface StandardCardProps {
  standard: StateStandardPerformanceData;
  momentumScoreGoal: number;
  smartGroup: string;
  classroomGrade: string;
  classroomId: number;
  studentId?: number | string;
  className: string;
}

const StandardCard: React.FC<StandardCardProps> = ({
  standard,
  momentumScoreGoal,
  smartGroup,
  studentId,
  classroomGrade,
  classroomId,
  className,
}) => {
  const { allSkillCategories, } = useAllSkillCategoriesAndSubCategoriesContext()
  const { name, avg_exposure, avg_ema, number_of_students, last_practiced, id, avg_questions_answered, grades, skills, description, } = standard

  const score = Math.round(avg_ema);
  const scoreClassName = smartGroup === NEEDS_MORE_PRACTICE ? smartGroupToClassName[getSmartGroup(avg_exposure, avg_ema, avg_questions_answered, momentumScoreGoal, true)] : smartGroupToClassName[smartGroup];

  const renderGradeTag = (grade: string) => {
    const gradeLevel = formatGradeLevelWithContext(grade)

    let className = 'grade-tag'
    let tooltipText

    if (grade === classroomGrade) {
      className += ' primary'
      tooltipText = `This standard is part of the ${gradeLevel} curriculum, which matches your class's grade.`
    } else {
      className += ' secondary'
      tooltipText = `This standard is part of the ${gradeLevel} curriculum.`
    }

    const tooltipTriggerText = <span className={className}>{gradeLevel}</span>

    return (
      <Tooltip
        tooltipText={tooltipText}
        tooltipTriggerText={tooltipTriggerText}
      />
    )
  }

  const renderSkillTag = (skill: SkillInterface, index: number) => {
    const { name, skill_sub_categories, } = skill
    const skillCategory = allSkillCategories.find(cat => cat.name === name);

    const skillsReportUrl = skillCategory
      ? `/teachers/progress_reports/skills_report/classroom/${classroomId}/smart_groups/by_skill?skill_category_id=${skillCategory.id}`
      : `/teachers/progress_reports/skills_report/classroom/${classroomId}/smart_groups/by_skill`;

    const tooltipText = skill_sub_categories.length > 0
      ? `This standard is aligned to the following Skill Sub-Categories within the <strong>${name}</strong> Skill Category:<br/><br/><strong>${skill_sub_categories.join(', ')}</strong><hr/>💡 <strong>Hint:</strong> Click this tag to view the Skills Report for this Skill Category`
      : `This standard is aligned to the <strong>${name}</strong> Skill Category.<hr/>💡 <strong>Hint:</strong> Click this tag to view the Skills Report for this Skill Category`;

    const skillTag = (
      <a
        className={`quill-button extra-small green contained focus-on-light skill-tag ${index === 0 ? 'first-skill-tag': ''}`}
        href={skillsReportUrl}
        rel="noopener noreferrer"
        target="_blank"
      >
        {name}
      </a>
    );

    return (
      <Tooltip
        key={name}
        tooltipText={tooltipText}
        tooltipTriggerText={skillTag}
      />
    )
  }

  return (
    <section className={`smart-group-card ${className}`}>
      <div className="name-and-last-practiced">
        <div className="standard-data">
          <div className="standard-header">
            <h3>
              {name}
            </h3>
            <div className="grades">
              <span className="sr-only">Grades: </span>
              {[...grades].sort(sortGradeLevels).map(grade => renderGradeTag(grade))}
            </div>
          </div>
          <div className="skill-tags">
            <span className="sr-only">Skill Categories: </span>
            {skills.map((skill, index) => renderSkillTag(skill, index))}
          </div>
          <span className="sr-only">Standard Description: </span>
          <TruncatedDescription description={standard.description} />
        </div>
        <p className="standard-practice-info">
          {studentId ? `Last practiced on ${formatLastPracticed(last_practiced)}` : `${number_of_students} ${pluralize(number_of_students, 'student', 'students')} practiced`}
        </p>
      </div>
      <div className="smart-group-metrics">
        <div className="metric">
          <h4 className="metric-label">{studentId ? '' : 'Avg. '}Momentum Score</h4>
          <span className={`metric-value score ${scoreClassName}`}>
            {formatMomentumScore(score, avg_exposure)}
          </span>
        </div>
        <div className="metric">
          <h4 className="metric-label">{studentId ? '' : 'Avg. '}Exposure</h4>
          <span className={`metric-value exposure ${avg_exposure.toLowerCase()}`}>
            {avg_exposure}
          </span>
        </div>
      </div>
    </section>
  );
};

interface SmartGroupSectionProps {
  standards: StateStandardPerformanceData[];
  totalStandards: number;
  momentumScoreGoal: number;
  smartGroup: string;
  classroomGrade: string;
  classroomId: number;
  studentId?: number | string;
}

// Also gates the "Show all" button, so a hidden card always has a way to be revealed.
const VISIBLE_STANDARDS_COUNT = 3;

const SmartGroupSection: React.FC<SmartGroupSectionProps> = ({
  classroomGrade,
  classroomId,
  standards,
  totalStandards,
  momentumScoreGoal,
  smartGroup,
  studentId,
}) => {
  const [showAllStandards, setShowAllStandards] = React.useState(false);

  const toggleShowAll = () => {
    setShowAllStandards(!showAllStandards);
  };
  
  // Sort standards by Momentum Score (highest to lowest) then by name
  const sortedStandards = React.useMemo(() => {
    return [...standards].sort((a, b) => {
      const aSort = getSortableSmartGroup(smartGroup, a.avg_ema, alphabeticalName(a.name));
      const bSort = getSortableSmartGroup(smartGroup, b.avg_ema, alphabeticalName(b.name));
      return aSort.localeCompare(bSort);
    });
  }, [standards, smartGroup]);

  const getTooltipText = (goal: number) => {
    switch (smartGroup) {
      case MEETING_GOAL:
        return `Standards whose average Momentum Score meets or exceeds your goal of ${goal} and have medium or high average exposure. You can change this goal at any time by clicking the 'Change goal' button.`;
      case APPROACHING_GOAL:
        return `Standards with an average Momentum Score between ${goal - 10} and ${goal - 1} and medium or high average exposure. Students are making steady progress on these standards.`;
      case NEEDS_SUPPORT:
        return `Standards with an average Momentum Score below ${goal - 10} despite medium or high average exposure. These standards may require targeted intervention.`;
      case NEEDS_MORE_PRACTICE:
        return `Standards with low average exposure (5 or fewer questions answered on average), regardless of score. Their average Momentum Score is not yet reliable.`;
      default:
        return '';
    }
  };

  const tooltipText = getTooltipText(momentumScoreGoal);

  return (
    <section className={`smart-group-section ${showAllStandards ? 'show-all' : ''}`}>
      <div className="smart-group-header">
        <div className="smart-group-title">
          <h2>{titleCaseMultiWordString(smartGroup)}</h2>
          <Tooltip
            tooltipText={tooltipText}
            tooltipTriggerText={<img alt="Help icon" src={helpIconSrc} />}
          />
        </div>
        <div className="smart-group-stats">
          <span>{totalStandards} {pluralize(totalStandards, 'standard', 'standards')}</span>
        </div>
      </div>
      <div className="smart-group-cards-grid">
        {sortedStandards.map((standard, index) => (
          <StandardCard
            className={index >= VISIBLE_STANDARDS_COUNT ? 'initially-hidden' : ''}
            classroomGrade={classroomGrade}
            classroomId={classroomId}
            key={standard.id}
            momentumScoreGoal={momentumScoreGoal}
            smartGroup={smartGroup}
            standard={standard}
            studentId={studentId}
          />
        ))}
      </div>
      {standards.length > VISIBLE_STANDARDS_COUNT && (
        <button 
          className="quill-button focus-on-light small outlined green" 
          onClick={toggleShowAll}
          type="button"
        >
          {showAllStandards ? 'Show fewer standards' : `Show all ${standards.length} standards`}
        </button>
      )}

    </section>
  );
};

const ByStandard: React.FC<ByStandardProps> = ({
  classroom,
  data,
  momentumScoreGoal,
  selectedStateStandard,
  selectedGradeLevel,
  studentId,
}) => {
  const navigate = useNavigate();

  if (!data) { return <Spinner /> }

  if (classroomHasNoStudents(classroom)) {
    return <NoStudentsEmptyState className="by-standard" />
  }

  let filteredStateStandards = data.filter(standard => standard.avg_questions_answered > 1 || standard.grades.find(grade => grade === classroom.grade))

  if (selectedGradeLevel) {
    filteredStateStandards = data.filter(standard => standard.grades.find(grade => grade === selectedGradeLevel))
  }

  // Group standards by smart group - only those that have been practiced
  const standardsWithPractice = filteredStateStandards.filter(standard => standard.avg_questions_answered > 0);
  const standardsByGroup = standardsWithPractice.reduce((groups, standard) => {
    const smartGroup = getSmartGroup(standard.avg_exposure, standard.avg_ema, standard.avg_questions_answered, momentumScoreGoal);
    if (!groups[smartGroup]) {
      groups[smartGroup] = [];
    }
    groups[smartGroup].push(standard);
    return groups;
  }, {} as Record<string, StateStandardPerformanceData[]>);
  
  const standardsNotPracticed = filteredStateStandards.filter(standard => standard.avg_questions_answered === 0);
  const hasUnpracticedStandards = standardsNotPracticed.length > 0;
  const allStandardsUnpracticed = standardsWithPractice.length === 0;

  const handleViewAllStandards = () => {
    navigate(`/teachers/progress_reports/standards_report/classroom/${classroom.id}/performance_breakdown/by_standard`);
  };
  const sharedProps = {
    momentumScoreGoal,
    selectedStateStandard,
    studentId,
    classroomGrade: classroom.grade,
    classroomId: classroom.id
  }

  if (allStandardsUnpracticed) {
    return (
      <div className="by-standard">
        <div className="empty-state">
          <h2>
            No standards matching your filters have been practiced yet
          </h2>
          <button 
            className="quill-button contained medium green focus-on-light"
            onClick={handleViewAllStandards}
            type="button"
          >
            View all standards in Performance Breakdown
          </button>
        </div>
      </div>
    )
  }

  const smartGroupSections = ORDERED_SMART_GROUPS.map(smartGroup => {
    if (!(standardsByGroup[smartGroup]?.length > 0)) { return }

    return (
      <SmartGroupSection
        {...sharedProps}
        key={smartGroup}
        smartGroup={smartGroup}
        standards={standardsByGroup[smartGroup]}
        totalStandards={standardsByGroup[smartGroup].length}
      />
    )
  }).filter(Boolean)

  const gradeToUseForGradeLevel = selectedGradeLevel || classroom.grade
  const gradeLevel = formatGradeLevelWithContext(gradeToUseForGradeLevel)

  return (
    <div className="by-standard">
      {smartGroupSections}
      {hasUnpracticedStandards && (
        <section className="unpracticed-section-wrapper">
          <div className="unpracticed-section">
            <h2>
              {standardsNotPracticed.length} {pluralize(standardsNotPracticed.length, 'standard', 'standards')} from {gradeLevel} {pluralize(standardsNotPracticed.length, "hasn't", "haven't")} been practiced yet
            </h2>
            <button 
              className="quill-button outlined medium grey focus-on-light"
              onClick={handleViewAllStandards}
              type="button"
            >
              View all standards in Performance Breakdown
            </button>
          </div>
        </section>
      )}
    </div>
  );
}

export default ByStandard
